import React, {
  createContext, useReducer, useContext, useCallback, useState, useEffect
} from 'react';
import { UploadStatusEnum } from './UploadConfig';
import { STUDY_UPLOAD_REPORT_TYPE } from '../../../utils/Utils';
import { ReferralService } from '../ReferralService';
import PropTypes from 'prop-types';
import { useBroadcastChannel } from '../../../context/BroadcastChannelContext';
import { BlobServiceClient } from '@azure/storage-blob';

// Actions
const ADD_FILE = 'ADD_FILE';
const REMOVE_FILE = 'REMOVE_FILE';
const ADD_ABORTCONTROLLER = 'ADD_ABORTCONTROLLER';
const UPDATE_PROGRESS = 'UPDATE_PROGRESS';



// Initial state
const initialContextState = {
  queue: [],
  abortControllerQueue: [],
};

// Reducer
function uploadReducer(state, action) {
  switch (action.type) {
    case ADD_ABORTCONTROLLER: {
      let controllerQueue = state.abortControllerQueue.filter(payload => payload.id !== action.payload.id);
      return { ...state, abortControllerQueue: [...controllerQueue, action.payload] };
    }
    case ADD_FILE: {
      let existingQueue = state.queue.filter(payload => payload.id !== action.payload.id);
      return { ...state, queue: [...existingQueue, action.payload] };
    }
    case REMOVE_FILE: {
      return {
        ...state,
        queue: state.queue.filter(payload => payload.id !== action.payload),
        abortControllerQueue: state.abortControllerQueue.filter(payload => payload.id !== action.payload),
      };
    }
    case UPDATE_PROGRESS: {
      return {
        ...state,
        queue: state.queue.map(payload =>
          payload.id === action.payload.id ? { ...payload, ...action.payload } : payload
        ),
      };
    }
    default:
      return state;
  }
  
}

// Context
const UploadQueueContext = createContext();

// Provider component
export function UploadQueueContextProvider({ children, apiConfig }) {
  const { sendMessage } = useBroadcastChannel();
  const [uploadContextState, dispatch] = useReducer(uploadReducer, initialContextState);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadConfig, setUploadConfig] = useState(null);
  const [refreshTable, setRefreshTable] = useState(false);
  const SELECT_FILE = 'SELECT_FILE'
  const UPLOAD_FILE = 'UPLOAD_FILE'
  const SUCCESS_FLAG = "SUCCESS"  

  const addToQueue = useCallback(payload => dispatch({ type: ADD_FILE, payload: payload }), []);
  const addAbortController = useCallback(payload => dispatch({ type: ADD_ABORTCONTROLLER, payload: payload }), []);

  const removeFile = useCallback(fileId => dispatch({ type: REMOVE_FILE, payload: fileId }), []);

  const getCurrentQueue = useCallback((trackingId) => {
    return uploadContextState?.queue?.find(({ id }) => id === trackingId)
  }, [uploadContextState?.queue]);

  const getCurrentQueueByStatus = useCallback(()=> {
    // Function to categorize the current queue exam data based on status and sent as broadcast message
    // sample output: { 'INPROGRESS': [64, 65], 'QUEUED': [6], 'COMPLETED': [70], "CANCELLED", [7 }
    const result = {};
    uploadContextState.queue.forEach(item => {
      const status = item.status;

      if (!result[status]) {
        result[status] = [];
      }
      result[status].push(item.id);
    });

    return result;
  },[uploadContextState.queue])
  
  const uploadComplete = useCallback((payload) => {
    dispatch({ type: UPDATE_PROGRESS, payload: payload })
    setIsUploading(false)
  }, []);

  const updateProgress = useCallback((payload) => {
    setIsUploading(true)
    dispatch({ type: UPDATE_PROGRESS, payload: payload })
  }, []);

  const updateFailed = useCallback((payload) => {
    dispatch({ type: UPDATE_PROGRESS, payload: payload })
    setIsUploading(false)
  }, []);

  // Initialize Azure Blob Storage Client
  const initializeStorageClient = useCallback(async () => {
    try {
      // Replace with your Azure Storage Account name and SAS token or account key
      const blobConfig = await ReferralService.getBlobConfig();
      const account = blobConfig.account_name;
      const sas = blobConfig.sas;
      let uploadConfig = {
        blobStorageClient: new BlobServiceClient(
          `https://${account}.blob.core.windows.net?${sas}`
        ),
        blobContainer: blobConfig.container_name,
        uploadOptions: {
          concurrency: blobConfig.concurrency,
          blocksize: blobConfig.blocksize,
        },
      };
      setUploadConfig(uploadConfig)
     
    } catch (error) {
      console.error('Azure Blob Storage initialization error:', error);
    }
  }, []);

  // -------------------------------------------------------------

  const formatFileSize = (bytes, base = 1000) => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    if (bytes === 0) return '0 Byte';
    const i = parseInt(Math.floor(Math.log(bytes) / Math.log(base)));
    const size = bytes / Math.pow(base, i)
    return size.toFixed(2) + sizes[i]
  }
  const auditEvents = useCallback((id, action_sub_type, reportType, fileType, status, reason, fileSize=null) => {
    let fileExt = ""
    if (reportType === "dicom") {
      reportType = STUDY_UPLOAD_REPORT_TYPE.DICOM;
      fileExt = STUDY_UPLOAD_REPORT_TYPE.ZIP_EXTENSION
    } else if (reportType === "report") {
      reportType = STUDY_UPLOAD_REPORT_TYPE.REPORT;
      fileExt = STUDY_UPLOAD_REPORT_TYPE.PDF_EXTENSION;
    }
    if (action_sub_type === SELECT_FILE && status === UploadStatusEnum.FAILED)
      fileExt = fileType

    let requestData = {
      'exam_id': id,
      'action_sub_type': action_sub_type,
      'upload_event': {
        'componentType': reportType,
        'fileType': fileExt,
        'status': status
      }
    }
    if (reason) {
      requestData['upload_event']['reason'] = reason
    }
    if (fileSize) {
      requestData['upload_event']['fileSize'] = formatFileSize(fileSize)
    }
    ReferralService.auditUploadEvent(requestData);
  }, [])

  const updateUploadStatus = (exam_id, report_type, status, failure_reason = null, progress_value = null, s3_path = null) => {
    let requestData = {
      'exam_id': exam_id,
      'report_type': report_type,
      'status': status,
      'failure_reason': failure_reason,
      'progress_value': progress_value,
      's3_path': s3_path
    }
    ReferralService.updateStudyUploadStatus(requestData);
  }

  const updateAuthStatus = (id) => {
    const data = {
      "exam_id_list": [
        id
      ],
      "exam_status": 'COLL_COMPLETE'
    }
    ReferralService.submitAuthInfo(data)
  }

  const getProgressValue = (progressValue) => (progressValue * 100).toFixed(0);

  const onUploadStartCallback = useCallback((id, fileInfo, uploadingFile) => {
    auditEvents(id, UPLOAD_FILE, uploadingFile.reportType, uploadingFile?.file?.type, UploadStatusEnum.STARTED, '', uploadingFile?.file?.size);
    uploadingFile.status = UploadStatusEnum.STARTED
    let s3_path = uploadingFile?.s3_dir_name + uploadingFile?.file?.name
    updateProgress({
      id,
      fileInfo: fileInfo.map((f) =>
        f.reportType === uploadingFile.reportType ? { ...f, ...uploadingFile } : f
      ),
      status: UploadStatusEnum.STARTED,
      isUploading: true
    });
    updateUploadStatus(id, uploadingFile.reportType, UploadStatusEnum.STARTED, null, null, s3_path)
  },[auditEvents, updateProgress])

  const onUploadProgressCallback = useCallback((progressValue, id, fileInfo, uploadingFile) => {
    if (progressValue < 1 && uploadingFile.status !== UploadStatusEnum.PAUSING) {
      uploadingFile.status = UploadStatusEnum.INPROGRESS
      uploadingFile.isUploading = true
      let progressVal = getProgressValue(progressValue)

      if (+progressVal > +uploadingFile?.progress) {
        uploadingFile.progress = progressVal
        updateProgress({
          id,
          fileInfo: fileInfo.map((f) =>
            f.reportType === uploadingFile.reportType ? { ...f, ...uploadingFile } : f
          ),
          status: UploadStatusEnum.INPROGRESS,
          isUploading: true
        });
        updateUploadStatus(id, uploadingFile.reportType, UploadStatusEnum.INPROGRESS, null, progressVal)
      }
      
    }
  },[updateProgress]);

  const onUploadCompleteCallback = useCallback((id, fileInfo, uploadingFile, file) => {
    console.log('upload completed:', file?.name)
    uploadingFile.status = UploadStatusEnum.COMPLETED
    uploadingFile.isUploading = false
    uploadingFile.progress = 100
    uploadComplete({
      id, fileInfo: fileInfo.map((f) =>
        f.reportType === uploadingFile.reportType ? { ...f, ...uploadingFile } : f
      ),
      status: UploadStatusEnum.COMPLETED,
      isUploading: true
    })
    updateUploadStatus(id, uploadingFile.reportType, UploadStatusEnum.COMPLETED, null, 100)
    
    auditEvents(id, UPLOAD_FILE, uploadingFile.reportType, uploadingFile?.file?.type, SUCCESS_FLAG, '');
    if (fileInfo?.every(res => res?.status === UploadStatusEnum.COMPLETED)) {
      updateAuthStatus(id);
      setRefreshTable(true)
    }
  },[auditEvents, uploadComplete])

  const onErrorCallback = useCallback((id, fileInfo, uploadingFile, error) => {
    console.log('on Upload error callback:')
    let auditMessageOnError = `${error}. Aborted the upload at ${uploadingFile?.progress}% progress.`
    auditEvents(id, UPLOAD_FILE, uploadingFile.reportType, uploadingFile?.file?.type, UploadStatusEnum.FAILED, auditMessageOnError);
    const failedInfo = {
      status: UploadStatusEnum.FAILED, isUploading: false
    }
    updateFailed({
      id,
      fileInfo: fileInfo.map((f) => (
        { ...f, ...failedInfo }
      )),
      status: UploadStatusEnum.FAILED,
      isUploading: false
    });
    updateUploadStatus(id, uploadingFile.reportType, UploadStatusEnum.FAILED, error)
  },[auditEvents, updateFailed])



  const onCancelCallback = useCallback((id, fileInfo, uploadingFile, file) => {
    let auditMessageOnCancel = `User aborted the upload at ${uploadingFile?.progress}% progress.`
    auditEvents(id, UPLOAD_FILE, uploadingFile.reportType, uploadingFile?.file?.type, UploadStatusEnum.FAILED, auditMessageOnCancel);
    console.log('upload cancelled', file.name)
    const failedInfo = {
      status: UploadStatusEnum.FAILED, isUploading: false, progress: null
    }
    updateUploadStatus(id, uploadingFile.reportType, UploadStatusEnum.FAILED, `Cancelled by the user at ${uploadingFile?.progress}% progress.`)
    updateFailed({
      id,
      fileInfo: fileInfo.map((f) => (
        { ...f, ...failedInfo }
      )),
      status: UploadStatusEnum.FAILED,
      isUploading: false
    });
  },[auditEvents, updateFailed])

  // -------------------------------------------------------------
  const uploadFile = useCallback((id, fileInfo, uploadingFile, abortController) => {
    if (!uploadConfig && !uploadConfig.blobStorageClient) return Promise.reject(new Error('Azure Blob Storage Client not initialized'));
    
    return new Promise((resolve, reject) => {
      if (!fileInfo || uploadingFile.status !== UploadStatusEnum.QUEUED) reject('Invalid file info /status ' +uploadingFile.status + ' '+fileInfo);

      const file = uploadingFile.file;
      const blobName = uploadingFile.s3_dir_name + file.name; // Use the same naming convention from original S3 code
      const blockBlobClient = uploadConfig.blobStorageClient
        .getContainerClient(uploadConfig.blobContainer)
        .getBlockBlobClient(blobName);

      try {
        onUploadStartCallback(id, fileInfo, uploadingFile);
        let blockSizeInMB = 4
        let concurrency = 32
        if(uploadConfig.uploadOptions.concurrency){
          concurrency = uploadConfig.uploadOptions.concurrency
        }
        if(uploadConfig.uploadOptions.blocksize){
          blockSizeInMB = uploadConfig.uploadOptions.blocksize
        }
        const blockSize = blockSizeInMB * 1024 * 1024; // block size in MB (adjust in env as needed)
        const options = {
          abortSignal:abortController.signal,
          blockSize: blockSize,
          concurrency: concurrency,
          onProgress: (ev) => {
            const progressValue = ev.loadedBytes / file.size;
            onUploadProgressCallback(
              progressValue,
              id,
              fileInfo,
              uploadingFile
            );
          },
        };
        blockBlobClient
          .uploadData(file, options)
          .then((response) => {
            console.log(JSON.stringify(response));
            onUploadCompleteCallback(id, fileInfo, uploadingFile, file);
            resolve(blobName);
          })
          .catch((error) => {
            if (error.name === "AbortError") {
              onCancelCallback(id, fileInfo, uploadingFile, file);
            } else{
              console.log(error);
              reject(error);
              onErrorCallback(id, fileInfo, uploadingFile, error);
            }
          });
      } catch (error) {
        onErrorCallback(id, fileInfo, uploadingFile, error);
        reject(error);
      }
    });
  }, [onCancelCallback, onErrorCallback, onUploadCompleteCallback, onUploadProgressCallback, onUploadStartCallback, uploadConfig]);

  useEffect(() => {
    initializeStorageClient();
  }, [initializeStorageClient]);

  useEffect(() => {
    const processQueue = async () => {
      for await (const { fileInfo, id, status } of uploadContextState.queue) {
        if (status === UploadStatusEnum.QUEUED) {
          // console.log('queued items:', uploadContextState.queue)
          setIsUploading(true);
          let abortController = new AbortController()
          addAbortController({
            id:id,
            abortController:abortController
          })
          //Upload DICOM
          await uploadFile(id, fileInfo, fileInfo[0], abortController);
          if (
            fileInfo.length > 1 &&
            fileInfo[1].status === UploadStatusEnum.QUEUED
          ){
            abortController = new AbortController()
            addAbortController({
              id:id,
              abortController:abortController
            })
            await uploadFile(id, fileInfo, fileInfo[1],abortController);
          }
          uploadComplete({
            id,
            status: UploadStatusEnum.COMPLETED,
            isUploading: false,
            isCompleted: true,
          });
          setIsUploading(false);
        }
      }
    };

    if (!isUploading) processQueue();
    if(uploadContextState?.queue.length > 0){
      sendMessage(getCurrentQueueByStatus());
    }
  }, [addAbortController, getCurrentQueueByStatus, isUploading, sendMessage, uploadComplete, uploadContextState.queue, uploadFile]);


  return (
    <UploadQueueContext.Provider value={{
      uploadContextState, isUploading, addToQueue, removeFile,
      uploadConfig, updateFailed, getCurrentQueue, apiConfig, auditEvents,
      refreshTable, setRefreshTable, getCurrentQueueByStatus, addAbortController, updateProgress, uploadComplete 
    }}>
      {children}
    </UploadQueueContext.Provider>
  );
}

UploadQueueContextProvider.propTypes = {
  children: PropTypes.any,
  apiConfig: PropTypes.object
};

// Custom hook to use the UploadContext
export function useUploadQueueContext() {
  return useContext(UploadQueueContext);
}